import React, { useEffect, useState } from "react";
import { Outlet, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { usePermissions } from "../../hooks/usePermission";
import Sidebar from "../sidebar/Sidebar";
import Topbar from "../topbar/Topbar";
import { getCurrentUser } from "../../services/userServices";
import { getStatus, getSubscription, initiatePayment } from "../../services/billingService";
import { UserContext } from "../../context/UserContext";
import { PendingApprovalsProvider } from "../../context/PendingApprovalsContext";
import styles from "./Layout.module.css";

function normalizeUser(data) {
  if (!data) return null;
  const fullName =
    data.name ||
    data.user_name ||
    [data.firstName, data.lastName].filter(Boolean).join(" ").trim();
  return {
    user_id: data.user_id,
    email: data.email,
    role: data.role,
    company_id: data.company_id,
    name: fullName,
    phone_number: data.phone_number,
    avatar_url: data.avatar_url,
    job_title: data.job_title,
    email_notifications: data.email_notifications,
    project_updates: data.project_updates,
    milestone_reminders: data.milestone_reminders,
    company_name: data.company_name,
    company_logo_url: data.logo_url,
    company_currency: data.company_currency || 'USD',
  };
}

export default function Layout() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { canManageBilling } = usePermissions();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [paywallForced, setPaywallForced] = useState(false);
  const [subStatus, setSubStatus] = useState(null);
  const [subDetails, setSubDetails] = useState(null);
  const [subscribing, setSubscribing] = useState(false);
  const [isNarrow, setIsNarrow] = useState(typeof window !== 'undefined' && window.innerWidth < 720);

  const fetchUser = async () => {
    try {
      const raw = await getCurrentUser();
      const u = normalizeUser(raw);
      setUser(u);
      return u;
    } catch (e) {
      console.error("Failed to load user:", e);
      throw e;
    }
  };

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        await fetchUser();
        const status = await getStatus().catch(() => ({ is_active: false, status: 'unknown' }));
        if (active) setSubStatus(status);
      } catch (e) {
        // Error already logged in fetchUser
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const handler = () => {
      // Backend now gates almost every API. A 402 means the subscription is
      // no longer active — refetch status and let the full-screen paywall take
      // over the whole layout instead of showing a dismissible modal-over-app.
      getStatus()
        .then((status) => setSubStatus(status))
        .catch(() => {})
        .finally(() => setPaywallForced(true));
    };
    window.addEventListener('subscription-required', handler);
    return () => window.removeEventListener('subscription-required', handler);
  }, []);

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 720);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    // Load the real usage-based cost breakdown whenever the paywall will render
    // (subscription inactive OR forced by a 402), so it matches the billing page
    // instead of falling back to the generic "starting at" price.
    const paywallVisible = paywallForced || (subStatus && !subStatus.is_active);
    if (paywallVisible && canManageBilling && subStatus?.status !== 'unknown') {
      getSubscription().then(setSubDetails).catch(() => {});
    }
  }, [subStatus, canManageBilling, paywallForced]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (loading) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.loading}>Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const handlePaywallSubscribe = async () => {
    setSubscribing(true);
    try {
      const { payfast_url, fields } = await initiatePayment();
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = payfast_url;
      Object.entries(fields).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    } catch {
      setSubscribing(false);
    }
  };

  if (!subStatus || !subStatus.is_active || paywallForced) {
    const isPastDue = subStatus?.status === 'past_due';
    const isUnknown = subStatus?.status === 'unknown';

    const headline = isUnknown
      ? 'Subscription Check Failed'
      : isPastDue
      ? 'Payment Issue'
      : canManageBilling
      ? 'Unlock Your Workspace'
      : 'Workspace Locked';

    const description = canManageBilling
      ? isUnknown
        ? 'We couldn\'t verify your subscription status. Please refresh the page or contact support if this persists.'
        : isPastDue
        ? 'Your last payment could not be processed. Subscribe again to restore full access to your team\'s workspace.'
        : 'Your free trial has ended. Subscribe now to keep your team productive without interruption.'
      : 'Your company\'s subscription is not currently active. Please contact your account administrator to restore access.';

    const features = [
      'Unlimited projects & milestones',
      'Resource & contractor management',
      'Project budgets & cost tracking',
      'Team scheduling & timesheets',
      'Real-time notifications',
    ];

    return (
      <div style={{
        position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg, #0c2d4e 0%, #185FA5 60%, #1a6bbf 100%)',
        padding: '24px', overflow: 'hidden', boxSizing: 'border-box', zIndex: 1,
      }}>
        {/* Decorative blur shapes */}
        <div style={{
          position: 'absolute', top: 0, right: 0, width: '400px', height: '400px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.07) 0%, transparent 70%)',
          borderRadius: '50%', pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: 0, left: 0, width: '400px', height: '400px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)',
          borderRadius: '50%', pointerEvents: 'none',
        }} />

        <div style={{
          background: 'white', borderRadius: '24px', maxWidth: isNarrow ? '440px' : '880px', width: '100%',
          boxShadow: '0 30px 60px -15px rgba(15, 23, 42, 0.15), 0 0 0 1px rgba(15, 23, 42, 0.04)',
          display: 'grid', gridTemplateColumns: isNarrow ? '1fr' : 'minmax(0, 1fr) minmax(0, 1fr)',
          overflow: 'hidden', position: 'relative', zIndex: 1,
        }}>
          {/* Left: Brand panel */}
          {!isNarrow && (
          <div style={{
            background: 'linear-gradient(135deg, #185FA5 0%, #0C447C 100%)',
            padding: '48px 40px', color: 'white', display: 'flex', flexDirection: 'column',
            justifyContent: 'space-between', position: 'relative', overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute', top: '-50%', right: '-30%', width: '300px', height: '300px',
              background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
              borderRadius: '50%',
            }} />
            <div style={{
              position: 'absolute', bottom: '-30%', left: '-20%', width: '250px', height: '250px',
              background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)',
              borderRadius: '50%',
            }} />

            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{
                width: '56px', height: '56px', borderRadius: '14px',
                background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: '32px',
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {isPastDue || isUnknown ? (
                    <>
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </>
                  ) : (
                    <>
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </>
                  )}
                </svg>
              </div>

              <h2 style={{
                fontSize: '1.75rem', fontWeight: 700, margin: '0 0 12px',
                letterSpacing: '-0.025em', lineHeight: 1.2,
              }}>
                Project management<br />that scales with you
              </h2>
              <p style={{
                fontSize: '0.9375rem', margin: '0 0 32px', opacity: 0.85, lineHeight: 1.6,
              }}>
                Everything your team needs to plan, track, and deliver projects on time and on budget.
              </p>

              <ul style={{
                listStyle: 'none', padding: 0, margin: 0, display: 'flex',
                flexDirection: 'column', gap: '12px',
              }}>
                {features.map((feature) => (
                  <li key={feature} style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    fontSize: '0.875rem', opacity: 0.95,
                  }}>
                    <div style={{
                      width: '20px', height: '20px', borderRadius: '50%',
                      background: 'rgba(255,255,255,0.2)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            <div style={{
              fontSize: '0.75rem', opacity: 0.6, marginTop: '32px', position: 'relative', zIndex: 1,
            }}>
              Powered by Nodel
            </div>
          </div>
          )}

          {/* Right: Action panel */}
          <div style={{
            padding: '48px 40px', display: 'flex', flexDirection: 'column',
            justifyContent: 'center',
          }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '4px 12px', borderRadius: '999px',
              background: isPastDue || isUnknown ? '#fef3c7' : '#dbeafe',
              color: isPastDue || isUnknown ? '#92400e' : '#1e40af',
              fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.05em',
              textTransform: 'uppercase', alignSelf: 'flex-start', marginBottom: '20px',
            }}>
              <span style={{
                width: '6px', height: '6px', borderRadius: '50%',
                background: isPastDue || isUnknown ? '#f59e0b' : '#3b82f6',
              }} />
              {isPastDue ? 'Action Required' : isUnknown ? 'Verify Subscription' : 'Trial Ended'}
            </div>

            <h1 style={{
              fontSize: '1.75rem', fontWeight: 700, color: '#0f172a',
              margin: '0 0 12px', letterSpacing: '-0.025em', lineHeight: 1.2,
            }}>
              {headline}
            </h1>
            <p style={{
              fontSize: '0.9375rem', color: '#64748b', lineHeight: 1.6,
              margin: '0 0 28px',
            }}>
              {description}
            </p>

            {canManageBilling && !isUnknown && (
              <div style={{
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                border: '1px solid #e2e8f0', borderRadius: '14px', padding: '20px',
                marginBottom: '24px',
              }}>
                <div style={{
                  fontSize: '0.6875rem', fontWeight: 700, color: '#64748b',
                  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px',
                }}>
                  {subDetails ? 'Your monthly cost' : 'Starting at'}
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                  <span style={{
                    fontSize: '2rem', fontWeight: 700, color: '#0f172a',
                    letterSpacing: '-0.03em',
                  }}>
                    R{subDetails ? Number(subDetails.amount).toFixed(2) : '100'}
                  </span>
                  <span style={{ fontSize: '0.9375rem', color: '#64748b' }}>/month</span>
                </div>

                {subDetails ? (
                  <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid #e2e8f0' }}>
                    <div style={{
                      display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem',
                      color: '#475569', padding: '4px 0',
                    }}>
                      <span>{subDetails.userCount} user{subDetails.userCount !== 1 ? 's' : ''} × R{Number(subDetails.pricing?.price_per_user || 80).toFixed(0)}</span>
                      <span style={{ fontWeight: 600 }}>R{(subDetails.userCount * Number(subDetails.pricing?.price_per_user || 80)).toFixed(0)}</span>
                    </div>
                    <div style={{
                      display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem',
                      color: '#475569', padding: '4px 0',
                    }}>
                      <span>{subDetails.totalResources} resource{subDetails.totalResources !== 1 ? 's' : ''}/contractor{subDetails.totalResources !== 1 ? 's' : ''} × R{Number(subDetails.pricing?.price_per_resource || 20).toFixed(0)}</span>
                      <span style={{ fontWeight: 600 }}>R{(subDetails.totalResources * Number(subDetails.pricing?.price_per_resource || 20)).toFixed(0)}</span>
                    </div>
                    {subDetails.appliedMinimum && (
                      <div style={{ fontSize: '0.75rem', color: '#f59e0b', marginTop: '6px' }}>
                        Minimum charge of R{subDetails.minimum} applied
                      </div>
                    )}
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '6px', lineHeight: 1.5 }}>
                      Calculated from your current usage. Charge updates each renewal.
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: '0.8125rem', color: '#64748b', marginTop: '8px', lineHeight: 1.5 }}>
                    R80/user · R20/resource or contractor · R100 minimum
                  </div>
                )}
              </div>
            )}

            {canManageBilling ? (
              <button
                onClick={handlePaywallSubscribe}
                disabled={subscribing || isUnknown}
                style={{
                  width: '100%', padding: '14px 24px', borderRadius: '12px', border: 'none',
                  background: isUnknown
                    ? '#94a3b8'
                    : 'linear-gradient(135deg, #185FA5 0%, #0C447C 100%)',
                  color: 'white', fontSize: '0.9375rem', fontWeight: 600,
                  cursor: subscribing || isUnknown ? 'not-allowed' : 'pointer',
                  opacity: subscribing ? 0.7 : 1,
                  boxShadow: isUnknown ? 'none' : '0 4px 14px rgba(24, 95, 165, 0.25)',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                }}
                onMouseEnter={(e) => {
                  if (!subscribing && !isUnknown) {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(24, 95, 165, 0.35)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = isUnknown ? 'none' : '0 4px 14px rgba(24, 95, 165, 0.25)';
                }}
              >
                {isUnknown ? (
                  'Refresh Page'
                ) : subscribing ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 0.8s linear infinite' }}>
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                    Redirecting to PayFast...
                  </>
                ) : (
                  <>
                    Subscribe Now
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </>
                )}
              </button>
            ) : (
              <a
                href="mailto:sales@nodel.co.za"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  padding: '14px 24px', borderRadius: '12px',
                  background: 'linear-gradient(135deg, #185FA5 0%, #0C447C 100%)',
                  color: 'white', fontSize: '0.9375rem', fontWeight: 600, textDecoration: 'none',
                  boxShadow: '0 4px 14px rgba(24, 95, 165, 0.25)',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                Contact Administrator
              </a>
            )}

            {isUnknown && (
              <button
                onClick={() => window.location.reload()}
                style={{
                  width: '100%', padding: '14px 24px', borderRadius: '12px', border: 'none',
                  background: 'linear-gradient(135deg, #185FA5 0%, #0C447C 100%)',
                  color: 'white', fontSize: '0.9375rem', fontWeight: 600, cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(24, 95, 165, 0.25)',
                  marginTop: canManageBilling ? '-56px' : 0,
                }}
              >
                Refresh Page
              </button>
            )}

            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #f1f5f9',
            }}>
              <span style={{ fontSize: '0.8125rem', color: '#94a3b8' }}>
                Signed in as <span style={{ color: '#475569', fontWeight: 500 }}>{user.email}</span>
              </span>
              <button
                onClick={handleLogout}
                style={{
                  background: 'none', border: 'none', color: '#64748b',
                  fontSize: '0.8125rem', fontWeight: 500, cursor: 'pointer', padding: 0,
                  textDecoration: 'underline', textUnderlineOffset: '2px',
                }}
              >
                Sign out
              </button>
            </div>
          </div>
        </div>

        {/* Inline keyframes for the loading spinner */}
        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
          @media (max-width: 720px) {
            .paywall-grid { grid-template-columns: 1fr !important; }
            .paywall-brand { display: none !important; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <PendingApprovalsProvider>
    <div className={styles.wrapper}>
      <div className={styles.topbar}>
        <Topbar user={user} />
      </div>
      <div
        className={`${styles.main} ${
          sidebarCollapsed ? styles.sidebarCollapsed : ""
        }`}
      >
        <div className={styles.sidebarWrapper}>
          <Sidebar
            collapsed={sidebarCollapsed}
            onToggle={() => setSidebarCollapsed((prev) => !prev)}
            user={user}
            onLogout={handleLogout}
          />
        </div>

        <div className={styles.content}>
          <UserContext.Provider value={{ user, refreshUser: fetchUser }}>
            <Outlet />
          </UserContext.Provider>
        </div>
      </div>
    </div>
    </PendingApprovalsProvider>
  );
}
